// Declaração de tipos para Microsoft Clarity
declare global {
  interface Window {
    clarity?: (action: string, ...args: any[]) => void;
  }
}

import { preserveItems, restorePreserved } from '../../util/local-storage-preserve.service';
import { APP_UPDATE_PENDING_KEY } from '../../../constants/app-update.constants';
import {
  concluirLogin,
  esquecerDadosDeOutraConta,
  getIdToken,
  isKeycloakSession,
  limparSessaoKeycloak,
  mesmaConta,
  tokenValido,
  urlDeLogout,
} from '../keycloak/keycloak.util';

export class AuthServerProvider {
  constructor(
    private $http,
    private $rootScope,
    private localStorageService,
    private PerfilResource,
    private URLAPILOGIN
  ) {
    "ngInject";
  }

  async login(user) {
    const response = await this.$http.post(this.URLAPILOGIN + "/login", {
      login: user.email,
      password: user.password,
      app: "WEBAPP",
    });

    response.role = "admin";
    response.data.pj = true;

    this.localStorageService.set("user", response.data);
    this.localStorageService.set("token", response.data.accessToken);

    const perfil = await this.PerfilResource.getUserData().$promise;
    const fullUser = Object.assign({}, response.data, perfil);

    this.localStorageService.set("user", fullUser);

    this.configureMicrosoftClarity(fullUser);

    return fullUser;
  }

  /**
   * Conclui o login pelo Keycloak: troca o code por tokens (o access token vai para ls.token) e monta o
   * usuario como no login por senha — a identidade da base legada vem do mesmo /me de sempre, agora
   * chamado com o JWT (o interceptor poe o Bearer). A pagina recarrega em seguida e le o usuario do storage.
   */
  async loginKeycloak(search: string) {
    const anterior = this.localStorageService.get("user");
    const claims = await concluirLogin(search);
    const emailDeLogin = claims.email || claims.preferred_username;

    // A session that ended on a 401 leaves its data behind for the other tabs. When another account signs
    // in, that data (public API token, last addresses, last order...) must not carry over to it. The /me
    // below may overwrite "email" with the customer's contact address, hence the separate emailDeLogin.
    if (anterior && !mesmaConta(anterior.emailDeLogin || anterior.email, emailDeLogin)) {
      esquecerDadosDeOutraConta();
    }

    const basico = {
      emailDeLogin,
      accessToken: this.getToken(),
      nome: claims.name || claims.preferred_username,
      email: claims.email || claims.preferred_username,
      pj: true,
      authProvider: 'keycloak',
    };
    this.localStorageService.set("user", basico);

    const perfil = await this.PerfilResource.getUserData().$promise;
    const fullUser = Object.assign({}, basico, perfil);

    this.localStorageService.set("user", fullUser);

    this.configureMicrosoftClarity(fullUser);

    return fullUser;
  }

  async logout() {
    if (isKeycloakSession()) {
      // Encerra a sessao SSO no Keycloak e volta para /login. A promise nunca resolve de proposito: quem
      // chamou continuaria (limpando estado, navegando para /login) e essa navegacao sobrescreveria a do
      // logout, deixando o Keycloak com a sessao viva.
      const idToken = getIdToken();
      const preservados = preserveItems();
      this.localStorageService.clearAll();
      restorePreserved(preservados);
      limparSessaoKeycloak();
      localStorage.removeItem(APP_UPDATE_PENDING_KEY);
      window.location.assign(urlDeLogout(idToken));
      return new Promise(() => {});
    }

    const response = await this.$http.get(this.URLAPILOGIN + "/logout");
    const preserved = preserveItems();
    this.localStorageService.clearAll();
    restorePreserved(preserved);

    // Limpar flag de atualização pendente durante logout manual
    // Isso evita reloads desnecessários após usuário fazer logout explicitamente
    localStorage.removeItem(APP_UPDATE_PENDING_KEY);

    return response.data;
  }

  getToken() {
    return this.localStorageService.get("token");
  }

  hasValidToken() {
    return !!this.getToken();
  }

  async authorize() {
    if (isKeycloakSession()) {
      // Com SSO a sessao vale enquanto o token (ou o refresh) valer. Rejeita quando o Keycloak
      // encerrou a sessao.
      await tokenValido();
      return;
    }

    // A session that did not come from Keycloak (a login-services token left in storage from before
    // the switch) is treated like an expired token: the login API no longer validates tokens.
    // isLoggedInAsync clears the storage and the user goes to /login, which sends them to Keycloak.
    // Not from /login itself: a Keycloak return there would lose its code.
    if (!window.location.pathname.endsWith("/login")) {
      window.location.href = "/login";
    }
    throw new Error("Legacy session: sign in with Keycloak");
  }

  /**
   * Configura Microsoft Clarity com User ID personalizado e Custom Tags
   */
  configureMicrosoftClarity(userData) {
    // Verifica se o Clarity está disponível
    if (typeof window.clarity === 'function') {
      try {
        if (userData.id) {
          window.clarity("identify", userData.id.toString());
        }
        window.clarity("set", "ns_on_platform", userData.nuvemShopStoreId ? "true" : "false");
      } catch (error) {
        console.error("Erro ao configurar Microsoft Clarity:", error);
      }
    } else {
      console.warn("Microsoft Clarity não encontrado. Configurações não foram aplicadas.");
    }
  }
}
